import { useDisclosure } from '@chakra-ui/react'
import { useQueryClient } from '@tanstack/react-query'
import { FC, useContext, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import DialogWindow from '@/components/DialogWindow'

import { ShotlistsService } from '@/services/shotlist.service'
import { TasksService } from '@/services/tasks.service'
import { UploadFormService } from '@/services/uploadForm.service'

import { STATUS, STEP_TYPE } from '@/constants/common'
import { FormUploader } from '@/features'
import { useFile } from '@/features/File/hooks/useFile'
import { getUploadFileItemViewState } from '@/features/Home/helpers/getUploadFileItemViewState'
import { useHome } from '@/features/Home/hooks/useHome'
import { useUploadFileItemOpen } from '@/features/Home/hooks/useUploadFileItemOpen'
import { useUploadFileProcess } from '@/features/Home/hooks/useUploadFileProcess'
import { useFormUploaderTaskSession } from '@/store/features/formUploader/useFormUploaderTaskSession'
import { IShotlist } from '@/types/shotlist.interface'
import { IFormValues } from '@/types/uploadForm.interface'

import { DraftState, DraftsContext } from '../DraftsContext'

import UploadFileCard from './UploadFileCard'
import { usePlatformHeaderState } from './hooks/usePlatformHeaderState'

export interface ITime {
	minutes: number
	seconds: number
}

interface IUploadFileItemProps {
	file?: File
	draftState: DraftState
	activeUploading: string
	setActiveUploading: (num: string) => void
	clearItem: (id: string) => void
	isModalOpen: boolean
	setIsModalOpen: (open: boolean) => void
}

const UploadFileItem: FC<IUploadFileItemProps> = ({
	file,
	draftState,
	activeUploading,
	setActiveUploading,
	clearItem,
	isModalOpen,
	setIsModalOpen
}) => {
	const { draft, controller } = draftState
	const { deleteTranslation } = useFile(draft?.id)
	const { open, onOpen, onClose } = useDisclosure()
	const { languages } = useHome()

	const [isTranslateLoading, setIsTranslateLoading] = useState(false)

	const { updateDraftStatus } = useContext(DraftsContext)

	const taskId = draft.id

	const {
		selectedWay,
		openFormModal,
		selectedTranscribingLang,
		selectedPlatformList,
		isUploading,
		isTranscribing,
		setOpenFormModal,
		setSelectedTranscribingLang,
		setSelectedWay,
		setSelectedPlatformList,
		setIsUploading,
		setIsTranscribing
	} = useFormUploaderTaskSession(taskId)

	const {
		progress,
		error,
		localHasVideo,
		isUploaded,
		timer,
		watchTask,
		clearWatch,
		clearTimer,
		handleStart,
		setLocalHasVideo
	} = useUploadFileProcess({
		file,
		draftState,
		selectedWay,
		setIsUploading,
		setIsTranscribing
	})

	// Item click to open UploaderForm, or to navigate to the Shotlist page
	const { handleItemClick } = useUploadFileItemOpen({
		taskId,
		draftStatus: draft.status,
		isUploading,
		setActiveUploading,
		setIsModalOpen,
		setSelectedWay,
		setOpenFormModal,
		clearItem
	})

	const {
		startPlatformProcessing,
		finishPlatformDone,
		finishPlatformAutoFilled,
		finishPlatformError
	} = usePlatformHeaderState(taskId)

	const queryClient = useQueryClient()

	const defaultLanguage = languages?.find(lang => lang.code === 'en')

	const { control, getValues } = useForm<IFormValues>({
		defaultValues: { language: defaultLanguage || null }
	})

	useEffect(() => {
		if (draft.status === STATUS.FINISHED || isUploaded) {
			setOpenFormModal(true)
		}
	}, [draft.status, isUploaded, setOpenFormModal])

	useEffect(() => {
		if (!file || draftState?.controller) return

		setActiveUploading(taskId)
		setIsModalOpen(true)
	}, [])

	// Define if it is Upload flow or not and set state IsUploaded
	useEffect(() => {
		const fromContentList = sessionStorage.getItem('openFormModal')
		if (fromContentList !== taskId) return

		const openFromHome = async () => {
			try {
				const draft = await ShotlistsService.getShotlist(taskId)

				if (!draft?.has_video) return

				const formData = await UploadFormService.getFormData(taskId)

				if (formData && formData.is_completed === false) {
					setActiveUploading(taskId)
					setIsModalOpen(true)
					setSelectedWay(STEP_TYPE.UPLOAD)
					setOpenFormModal(true)
				}
			} catch (error) {
				console.log(error)
			} finally {
				sessionStorage.removeItem('openFormModal')
			}
		}

		openFromHome()
	}, [taskId])

	useEffect(() => {
		if (
			activeUploading === taskId &&
			isModalOpen &&
			draft.status === STATUS.FINISHED &&
			selectedWay === ''
		) {
			UploadFormService.getFormData(taskId)
				.then(formData => {
					if (formData) {
						if (formData.is_completed === false) {
							setSelectedWay(STEP_TYPE.UPLOAD)
						}
					} else {
						console.log(
							'Form is completed or status unknown, keeping default view'
						)
					}
				})
				.catch(() => {
					console.log('Form data not found (404), keeping default view')
				})
		}
	}, [activeUploading, isModalOpen, taskId, draft.status, selectedWay])

	// Deleting item
	const handleClick = async () => {
		controller?.abort()
		clearItem(taskId)
		setIsModalOpen(false)
		onClose()

		await TasksService.deleteTask(taskId)
		await queryClient.invalidateQueries({ queryKey: ['getTasks'] })
	}

	// Start file uploading and transcription
	const handleNextClick = (language: string) => {
		if (selectedWay === STEP_TYPE.TRANSCRIBE) {
			setIsModalOpen(false)
		}
		handleStart(language)
	}

	const isSuccess =
		selectedWay === STEP_TYPE.UPLOAD
			? progress === 100 && !isUploading
			: draft.status === STATUS.FINISHED || isUploaded

	const isMP4 =
		file?.type.toLowerCase() === 'video/mp4' ||
		file?.name.split('.').pop()?.toLowerCase() === 'mp4'

	const viewState = getUploadFileItemViewState({
		selectedWay,
		progress,
		isUploading,
		isTranscribing,
		isUploaded,
		error,
		hasFile: !!file,
		localHasVideo,
		draftStatus: draft.status,
		draftHasVideo: draft.has_video,
		timer
	})

	return (
		<>
			<UploadFileCard
				source={draft.source}
				isActive={activeUploading === taskId}
				viewState={viewState}
				onClick={handleItemClick}
				onCancelClick={onOpen}
			/>

			<DialogWindow
				open={open}
				taskId={taskId}
				handleClick={handleClick}
				onClose={onClose}
				titleText='Cancel Uploading?'
				noButtonText='No'
				yesButtonText='Yes'
				noButtonClassname='bg-gray-100 hover:bg-gray-200'
				yesButtonClassname='bg-gray-100 hover:bg-gray-200'
				yesButtonClassnameText='text-red-450'
			/>
			{isModalOpen && (
				<FormUploader
					setIsModalOpen={setIsModalOpen}
					control={control}
					getValues={getValues}
					languages={languages}
					taskId={taskId}
					onNextClick={handleNextClick}
					selectedWay={selectedWay}
					setSelectedWay={setSelectedWay}
					status={draft.status}
					error={error}
					openFormModal={openFormModal}
					setOpenFormModal={setOpenFormModal}
					isUpload={isUploaded}
					isSuccess={isSuccess}
					isMP4={isMP4}
					filename={file?.name || draft?.source}
					isUploading={isUploading}
					isTranscribing={isTranscribing}
					selectedTranscribingLang={selectedTranscribingLang}
					setSelectedTranscribingLang={setSelectedTranscribingLang}
					isTranslating={isTranslateLoading}
					setIsTranslating={setIsTranslateLoading}
					selectedPlatformList={selectedPlatformList}
					setSelectedPlatformList={setSelectedPlatformList}
					startPlatformProcessing={startPlatformProcessing}
					finishPlatformAutoFilled={finishPlatformAutoFilled}
					finishPlatformDone={finishPlatformDone}
					finishPlatformError={finishPlatformError}
					shotlist={draft as unknown as IShotlist}
					clearWatch={clearWatch}
					deleteTranslation={deleteTranslation}
					watchTask={watchTask}
					clearTimer={clearTimer}
					setIsUploading={setIsUploading}
					updateDraftStatus={updateDraftStatus}
					setLocalHasVideo={setLocalHasVideo}
					setIsTranscribing={setIsTranscribing}
				/>
			)}
		</>
	)
}
export default UploadFileItem
